<script lang="ts">
  import type { GameServer } from "@/api";
  import { gameLabel, statusLabels, serverStatusHint, transitionalStatuses } from "@/config";
  import { routeHref } from "@/router";
  import { isBillingSettlementPending } from "@/server-state-policy";

  interface Props {
    servers: GameServer[];
    serverLimit: number;
    totalCount?: number;
    busyAction: string | null;
    onAction: (server: GameServer, action: "start" | "stop") => void;
  }

  const {
    servers,
    serverLimit,
    totalCount = servers.length,
    busyAction,
    onAction,
  }: Props = $props();
</script>

<section class="panel-card server-panel" aria-labelledby="servers-title">
  <header class="panel-heading">
    <div>
      <p class="eyebrow">Твоя флотилия</p>
      <h2 id="servers-title">Серверы</h2>
    </div>
    <span class="panel-count">{totalCount} / {serverLimit}</span>
  </header>

  {#if servers.length === 0}
    <div class="empty-state">
      <span class="empty-orbit" aria-hidden="true"></span>
      <h3>Орбита пуста</h3>
      <p>Создай первый игровой сервер и пригласи друзей в свой мир.</p>
      <a href={routeHref("server-create")}>Создать сервер <span aria-hidden="true">→</span></a>
    </div>
  {:else}
    <div class="server-list">
      {#each servers as server (server.id)}
        {@const running = server.status === "running" || server.status === "starting"}
        {@const transitioning =
          transitionalStatuses.has(server.status) && server.status !== "starting"}
        {@const action = running ? "stop" : "start"}
        {@const billingPending = isBillingSettlementPending(server)}
        {@const actionBusy =
          busyAction === `start:${server.id}` || busyAction === `stop:${server.id}`}
        <article class="server-row">
          <a class="server-main" href={routeHref("server-home", { id: server.id })}>
            <span
              class={`game-symbol ${server.game.startsWith("minecraft") ? "cube-symbol" : "tree-symbol"}`}
              aria-hidden="true"
            ></span>
            <span class="server-info">
              <strong>{server.name}</strong>
              <span>{gameLabel(server.game)} / {server.core} / {server.version}</span>
              {#if transitionalStatuses.has(server.status) || server.status === "failed"}
                <span>{serverStatusHint(server.status, server.queuePosition)}</span>
              {/if}
            </span>
            <span class={`server-status ${server.status}`}>
              <i aria-hidden="true"></i>{statusLabels[server.status] ?? server.status}
            </span>
          </a>
          <div class="server-actions">
            <button
              class={`icon-button ${running ? "stop" : "play"}`}
              type="button"
              disabled={transitioning || billingPending || actionBusy}
              onclick={() => onAction(server, action)}
              aria-label={running ? `Остановить ${server.name}` : `Запустить ${server.name}`}
              title={billingPending ? "Завершаем расчёт…" : running ? "Остановить" : "Запустить"}
            >
              {billingPending
                ? "Расчёт…"
                : actionBusy
                  ? "Отправляем…"
                  : running
                    ? "Остановить"
                    : "Запустить"}
            </button>
            <a
              class="chevron"
              href={routeHref("server-home", { id: server.id })}
              aria-label={`Открыть ${server.name}`}>→</a
            >
          </div>
        </article>
      {/each}
    </div>
  {/if}
</section>

<style>
  .server-panel {
    min-height: 23rem;
  }

  a.server-main {
    text-decoration: none;
  }

  .server-actions {
    display: flex;
    flex: 0 0 auto;
    gap: 0.5rem;
  }

  .icon-button {
    display: grid;
    min-height: 44px;
    padding: 0.6rem 0.75rem;
    place-items: center;
    border: 1px solid var(--line);
    color: var(--muted);
    background: transparent;
    font-size: 0.8rem;
    transition:
      border-color 160ms ease,
      color 160ms ease,
      background 160ms ease;
  }

  .icon-button:hover:not(:disabled) {
    border-color: var(--mars-light);
    color: var(--mars-light);
    background: rgb(214 75 50 / 0.06);
  }

  .icon-button.play {
    color: var(--green);
  }

  .icon-button.stop {
    color: var(--mars-light);
  }
</style>
